// ============================================================
// SALT — Custom Date/Time Picker
// Only shows 15-minute increments (00, 15, 30, 45).
// Replaces native <input type="datetime-local">.
// Panel expands inline within the page flow (no overlay).
// ============================================================

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

// —— Types ——————————————————————————————————————

interface DateTimePickerProps {
  value: string;                     // YYYY-MM-DDTHH:MM format
  onChange: (value: string) => void;
  minDate?: string;                  // YYYY-MM-DDTHH:MM
  maxDate?: string;                  // YYYY-MM-DDTHH:MM
  hasError?: boolean;
  id?: string;
}

interface CalendarDay {
  date: Date;
  day: number;
  inMonth: boolean;
  disabled: boolean;
  isToday: boolean;
  isSelected: boolean;
}

// —— Constants ——————————————————————————————————

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MINUTES_OPTIONS = [0, 15, 30, 45];
const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

// —— Helpers ————————————————————————————————————

function toLocalStr(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${da}T${h}:${mi}`;
}

function parseLocal(s: string): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function formatDisplay(value: string): string {
  const d = parseLocal(value);
  if (!d) return '';
  const month = MONTHS[d.getMonth()].slice(0, 3);
  const day = d.getDate();
  const year = d.getFullYear();
  let h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${month} ${day}, ${year}  ·  ${h}:${min} ${ampm}`;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

// —— Component ——————————————————————————————————

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value, onChange, minDate, maxDate, hasError, id,
}) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'date' | 'time'>('date');
  const containerRef = useRef<HTMLDivElement>(null);

  // Store value on open so Cancel can revert
  const [valueOnOpen, setValueOnOpen] = useState(value);

  // Calendar view month
  const selected = parseLocal(value);
  const [viewYear, setViewYear] = useState(selected?.getFullYear() ?? new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(selected?.getMonth() ?? new Date().getMonth());

  const minD = parseLocal(minDate ?? '');
  const maxD = parseLocal(maxDate ?? '');

  // Sync calendar view when value changes externally
  useEffect(() => {
    const d = parseLocal(value);
    if (d) { setViewYear(d.getFullYear()); setViewMonth(d.getMonth()); }
  }, [value]);

  // Track value when opening
  const handleToggleOpen = useCallback(() => {
    if (!open) {
      setValueOnOpen(value);
      setActiveTab('date');
    }
    setOpen(!open);
  }, [open, value]);

  // Cancel: revert to value before opening
  const handleCancel = useCallback(() => {
    onChange(valueOnOpen);
    setOpen(false);
  }, [valueOnOpen, onChange]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { handleCancel(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, handleCancel]);

  // —— Calendar days ————————————————————————————
  const calendarDays = useMemo((): CalendarDay[] => {
    const days: CalendarDay[] = [];
    const totalDays = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfWeek(viewYear, viewMonth);
    const today = new Date();

    const prevMonthDays = getDaysInMonth(viewYear, viewMonth - 1);
    for (let i = firstDay - 1; i >= 0; i--) {
      const d = new Date(viewYear, viewMonth - 1, prevMonthDays - i);
      days.push({ date: d, day: prevMonthDays - i, inMonth: false, disabled: true, isToday: false, isSelected: false });
    }

    for (let day = 1; day <= totalDays; day++) {
      const d = new Date(viewYear, viewMonth, day);
      const dayStart = new Date(viewYear, viewMonth, day, 0, 0, 0);
      const dayEnd = new Date(viewYear, viewMonth, day, 23, 59, 59);

      let disabled = false;
      if (minD && dayEnd < new Date(minD.getFullYear(), minD.getMonth(), minD.getDate(), 0, 0, 0)) disabled = true;
      if (maxD && dayStart > new Date(maxD.getFullYear(), maxD.getMonth(), maxD.getDate(), 23, 59, 59)) disabled = true;

      days.push({
        date: d, day, inMonth: true, disabled,
        isToday: sameDay(d, today),
        isSelected: selected ? sameDay(d, selected) : false,
      });
    }

    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(viewYear, viewMonth + 1, i);
      days.push({ date: d, day: i, inMonth: false, disabled: true, isToday: false, isSelected: false });
    }

    return days;
  }, [viewYear, viewMonth, selected, minD, maxD]);

  // —— Month navigation ————————————————————————
  const prevMonth = useCallback(() => {
    setViewMonth((m) => { if (m === 0) { setViewYear((y) => y - 1); return 11; } return m - 1; });
  }, []);

  const nextMonth = useCallback(() => {
    setViewMonth((m) => { if (m === 11) { setViewYear((y) => y + 1); return 0; } return m + 1; });
  }, []);

  // —— Select date ——————————————————————————————
  const handleSelectDate = useCallback((day: CalendarDay) => {
    if (day.disabled) return;
    const cur = selected ?? new Date();
    const d = new Date(day.date);
    d.setHours(cur.getHours(), cur.getMinutes(), 0, 0);

    if (!selected) {
      const now = new Date();
      now.setMinutes(now.getMinutes() + 5);
      const remainder = now.getMinutes() % 15;
      if (remainder !== 0) now.setMinutes(now.getMinutes() + (15 - remainder));
      now.setSeconds(0, 0);
      d.setHours(now.getHours(), now.getMinutes());
    }

    if (minD && d < minD) { d.setHours(minD.getHours(), minD.getMinutes()); }
    if (maxD && d > maxD) { d.setHours(maxD.getHours(), maxD.getMinutes()); }

    onChange(toLocalStr(d));
    setActiveTab('time');
  }, [selected, onChange, minD, maxD]);

  // —— Time selection ———————————————————————————
  const selectedHour12 = selected ? (selected.getHours() % 12 || 12) : null;
  const selectedAmPm = selected ? (selected.getHours() >= 12 ? 'PM' : 'AM') : null;
  const selectedMinute = selected?.getMinutes() ?? null;

  const handleSelectHour = useCallback((h12: number) => {
    if (!selected) return;
    const d = new Date(selected);
    const isPM = d.getHours() >= 12;
    let h24 = h12 === 12 ? 0 : h12;
    if (isPM) h24 += 12;
    d.setHours(h24);
    onChange(toLocalStr(d));
  }, [selected, onChange]);

  const handleSelectMinute = useCallback((min: number) => {
    if (!selected) return;
    const d = new Date(selected);
    d.setMinutes(min);
    if (minD && d < minD) return;
    if (maxD && d > maxD) return;
    onChange(toLocalStr(d));
  }, [selected, onChange, minD, maxD]);

  const handleToggleAmPm = useCallback(() => {
    if (!selected) return;
    const d = new Date(selected);
    const h = d.getHours();
    d.setHours(h >= 12 ? h - 12 : h + 12);
    if (minD && d < minD) return;
    if (maxD && d > maxD) return;
    onChange(toLocalStr(d));
  }, [selected, onChange, minD, maxD]);

  // —— Disabled checks ————————————————————————
  const isTimeDisabled = useCallback((h12: number, min: number, ampm: 'AM' | 'PM'): boolean => {
    if (!selected) return true;
    const d = new Date(selected);
    let h24 = h12 === 12 ? 0 : h12;
    if (ampm === 'PM') h24 += 12;
    d.setHours(h24, min, 0, 0);
    if (minD && d < minD) return true;
    if (maxD && d > maxD) return true;
    return false;
  }, [selected, minD, maxD]);

  const isHourDisabled = useCallback((h12: number): boolean => {
    const curAmPm = selectedAmPm ?? 'AM';
    return MINUTES_OPTIONS.every((m) => isTimeDisabled(h12, m, curAmPm as 'AM' | 'PM'));
  }, [selectedAmPm, isTimeDisabled]);

  const isMinuteDisabled = useCallback((min: number): boolean => {
    if (!selected) return true;
    const h12 = selectedHour12 ?? 12;
    const ampm = selectedAmPm ?? 'AM';
    return isTimeDisabled(h12, min, ampm as 'AM' | 'PM');
  }, [selected, selectedHour12, selectedAmPm, isTimeDisabled]);

  const isAmPmDisabled = useCallback((target: 'AM' | 'PM'): boolean => {
    if (!selected) return true;
    return HOURS_12.every((h) => MINUTES_OPTIONS.every((m) => isTimeDisabled(h, m, target)));
  }, [selected, isTimeDisabled]);

  // —— Render ———————————————————————————————————
  return (
    <div ref={containerRef} className={`sl-dtp ${open ? 'sl-dtp--open' : ''}`} id={id}>
      {/* Trigger button */}
      <button
        type="button"
        className={`sl-dtp-trigger sl-input ${hasError ? 'sl-input--error' : ''}`}
        onClick={handleToggleOpen}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <svg className="sl-dtp-trigger-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="2" y="3" width="12" height="11" rx="2" stroke="currentColor" strokeWidth="1.4" />
          <path d="M2 7h12M5 1.5v3M11 1.5v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
        <span className={`sl-dtp-trigger-text ${value ? '' : 'sl-dtp-trigger-text--placeholder'}`}>
          {value ? formatDisplay(value) : 'Select date & time'}
        </span>
        <svg className="sl-dtp-trigger-chevron" width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M4 5.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Inline expandable panel — sits in page flow, not overlaid */}
      {open && (
        <div className="sl-dtp-panel" role="dialog" aria-label="Date and time picker">
          {/* Tab bar */}
          <div className="sl-dtp-tabs">
            <button
              type="button"
              className={`sl-dtp-tab ${activeTab === 'date' ? 'sl-dtp-tab--active' : ''}`}
              onClick={() => setActiveTab('date')}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <rect x="1.5" y="2.5" width="11" height="9.5" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
                <path d="M1.5 5.5h11M4.5 1v3M9.5 1v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
              Date
            </button>
            <button
              type="button"
              className={`sl-dtp-tab ${activeTab === 'time' ? 'sl-dtp-tab--active' : ''}`}
              onClick={() => setActiveTab('time')}
              disabled={!selected}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.3" />
                <path d="M7 4v3.5l2.5 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Time
            </button>
          </div>

          {/* Date panel */}
          {activeTab === 'date' && (
            <div className="sl-dtp-calendar">
              <div className="sl-dtp-cal-header">
                <button type="button" className="sl-dtp-cal-nav" onClick={prevMonth} aria-label="Previous month">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M8.5 3.5l-4 3.5 4 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <span className="sl-dtp-cal-title">{MONTHS[viewMonth]} {viewYear}</span>
                <button type="button" className="sl-dtp-cal-nav" onClick={nextMonth} aria-label="Next month">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M5.5 3.5l4 3.5-4 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              <div className="sl-dtp-cal-weekdays">
                {DAYS_SHORT.map((d) => <span key={d} className="sl-dtp-cal-weekday">{d}</span>)}
              </div>
              <div className="sl-dtp-cal-grid">
                {calendarDays.map((day, i) => (
                  <button
                    key={i}
                    type="button"
                    className={[
                      'sl-dtp-cal-day',
                      !day.inMonth ? 'sl-dtp-cal-day--outside' : '',
                      day.disabled ? 'sl-dtp-cal-day--disabled' : '',
                      day.isToday ? 'sl-dtp-cal-day--today' : '',
                      day.isSelected ? 'sl-dtp-cal-day--selected' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleSelectDate(day)}
                    disabled={day.disabled}
                    aria-label={day.date.toDateString()}
                  >
                    {day.day}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Time panel */}
          {activeTab === 'time' && selected && (
            <div className="sl-dtp-time">
              <div className="sl-dtp-time-columns">
                <div className="sl-dtp-time-col">
                  <span className="sl-dtp-time-col-label">Hour</span>
                  <div className="sl-dtp-time-options">
                    {HOURS_12.map((h) => (
                      <button
                        key={h}
                        type="button"
                        className={`sl-dtp-time-btn ${selectedHour12 === h ? 'sl-dtp-time-btn--active' : ''} ${isHourDisabled(h) ? 'sl-dtp-time-btn--disabled' : ''}`}
                        onClick={() => handleSelectHour(h)}
                        disabled={isHourDisabled(h)}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sl-dtp-time-col">
                  <span className="sl-dtp-time-col-label">Min</span>
                  <div className="sl-dtp-time-options">
                    {MINUTES_OPTIONS.map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={`sl-dtp-time-btn ${selectedMinute === m ? 'sl-dtp-time-btn--active' : ''} ${isMinuteDisabled(m) ? 'sl-dtp-time-btn--disabled' : ''}`}
                        onClick={() => handleSelectMinute(m)}
                        disabled={isMinuteDisabled(m)}
                      >
                        {String(m).padStart(2, '0')}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sl-dtp-time-col">
                  <span className="sl-dtp-time-col-label">Period</span>
                  <div className="sl-dtp-time-options">
                    {(['AM', 'PM'] as const).map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`sl-dtp-time-btn ${selectedAmPm === p ? 'sl-dtp-time-btn--active' : ''} ${isAmPmDisabled(p) ? 'sl-dtp-time-btn--disabled' : ''}`}
                        onClick={() => { if (selectedAmPm !== p) handleToggleAmPm(); }}
                        disabled={isAmPmDisabled(p)}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="sl-dtp-time-preview">
                {formatDisplay(value)}
              </div>
            </div>
          )}

          {/* Footer: Cancel + Done */}
          <div className="sl-dtp-footer">
            <button type="button" className="sl-dtp-cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="sl-dtp-done-btn"
              onClick={() => setOpen(false)}
              disabled={!selected}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;